import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DeliveryService } from '../delivery.service';
import { RiderService } from '../../riders/rider.service';
import { VehicleService } from '../../vehicles/vehicle.service';
import Swal from 'sweetalert2';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-delivery-dialog',
  templateUrl: './delivery-dialog.component.html',
  styleUrls: ['./delivery-dialog.component.scss']
})
export class DeliveryDialogComponent implements OnInit {
  form: FormGroup;
  loading = false;
  riders: any[] = [];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DeliveryDialogComponent>,
    private deliveryService: DeliveryService,
    private riderService: RiderService,
    private vehicleService: VehicleService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.form = this.fb.group({
      customerName: ['', Validators.required],
      contactNumber: ['', Validators.required],
      driverName: ['', Validators.required], // will store rider name
      driverId: [null, Validators.required], // hidden actual ID
      vehicleNumber: ['', Validators.required],
      pickupLocation: ['', Validators.required],
      deliveries: this.fb.array([this.createDeliveryGroup()])
    });
  }

  ngOnInit(): void {
    this.loadActiveRiders();
  }

  /** ✅ Load only active riders */
  loadActiveRiders(): void {
    this.loading = true;
    this.riderService.getRiders().pipe(finalize(() => (this.loading = false))).subscribe({
      next: (riders) => {
        this.riders = riders.filter((r: any) => r.isActive);
        console.log('%c[DeliveryDialog] ✅ Active riders loaded:', 'color: green', this.riders);
      },
      error: (err) => {
        console.error('[DeliveryDialog] ❌ Failed to load riders:', err);
        Swal.fire('Error', 'Failed to load active riders', 'error');
      }
    });
  }

  /** ✅ When rider selected, auto-populate vehicle number */
  // onRiderSelected(riderId: number): void {
  //   const selected = this.riders.find((r) => r.id === riderId);
  //   if (selected) {
  //     this.form.patchValue({
  //       driverId: riderId,
  //       driverName: selected.fullName || '',
  //       vehicleNumber: selected.vehicles?.[0]?.plateNumber || ''
  //     });
  //     console.log('%c[DeliveryDialog] 🚘 Rider selected, vehicle auto-filled:', 'color: teal', selected);
  //   }
  // }
  onRiderSelected(riderId: number): void {
    const selected = this.riders.find((r) => r.id === riderId);
  
    if (selected) {
      console.log('%c[DeliveryDialog] 🧍 Rider selected:', 'color: blue', selected);
  
      this.form.patchValue({
        driverId: riderId,
        driverName: selected.fullName || '',
        vehicleNumber: '' // clear before lookup
      });
  
      // ✅ Now fetch assigned vehicle by riderId
      this.vehicleService.getVehicles().subscribe({
        next: (vehicles) => {
          const assigned = vehicles.find((v: any) => v.riderId === riderId);
          if (assigned) {
            console.log('%c[DeliveryDialog] 🚘 Vehicle found for rider:', 'color: green', assigned);
            this.form.patchValue({ vehicleNumber: assigned.plateNumber });
          } else {
            console.warn('%c[DeliveryDialog] ⚠️ No vehicle assigned to this rider.', 'color: orange');
            Swal.fire('No Vehicle', 'This rider has no assigned vehicle.', 'info');
          }
        },
        error: (err) => {
          console.error('[DeliveryDialog] ❌ Failed to fetch vehicles:', err);
          Swal.fire('Error', 'Failed to fetch vehicle data', 'error');
        }
      });
    }
  }
  

  /** ✅ Create new delivery entry group */
  private createDeliveryGroup(): FormGroup {
    return this.fb.group({
      item: ['', Validators.required],
      deliveryLocation: ['', Validators.required],
      receiverContact: ['', Validators.required],
      notes: [''],
      amount: [0, [Validators.required, Validators.min(1)]]
    });
  }

  /** ✅ Get the delivery FormArray */
  get deliveries(): FormArray {
    return this.form.get('deliveries') as FormArray;
  }

  /** ✅ Add new delivery row */
  addDelivery(): void {
    this.deliveries.push(this.createDeliveryGroup());
  }

  /** ✅ Remove a delivery row */
  removeDelivery(index: number): void {
    if (this.deliveries.length > 1) this.deliveries.removeAt(index);
  }

  /** ✅ Save each delivery as separate record */
  onSave(): void {
    if (this.form.invalid) {
      Swal.fire('Validation', 'Please fill all required fields.', 'warning');
      return;
    }

    this.loading = true;
    const baseData = {
      customerName: this.form.value.customerName,
      contactNumber: this.form.value.contactNumber,
      driverName: this.form.value.driverName,
      vehicleNumber: this.form.value.vehicleNumber,
      pickupLocation: this.form.value.pickupLocation
    };

    const deliveries = this.form.value.deliveries;
    console.log('%c[DeliveryDialog] 🧾 Prepared deliveries:', 'color: #3f51b5', deliveries);

    let completed = 0;
    const total = deliveries.length;

    deliveries.forEach((delivery, index) => {
      const payload = { ...baseData, ...delivery };
      console.log(`%c[DeliveryDialog] 🚚 Sending #${index + 1}`, 'color: green', payload);

      this.deliveryService.create(payload)
        .pipe(finalize(() => {
          completed++;
          if (completed === total) {
            this.loading = false;
            Swal.fire({
              icon: 'success',
              title: 'Success',
              text: `${total} delivery order${total > 1 ? 's' : ''} created successfully.`,
              confirmButtonText: 'OK'
            }).then(() => this.dialogRef.close(true));
          }
        }))
        .subscribe({
          next: () => console.log(`[DeliveryDialog] ✅ Delivery #${index + 1} saved successfully.`),
          error: (err) => {
            console.error(`[DeliveryDialog] ❌ Error saving delivery #${index + 1}:`, err);
            Swal.fire('Error', `Failed to save delivery #${index + 1}.`, 'error');
          }
        });
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
